"use client";

import { useRouter, useSearchParams } from "next/navigation";
import { LoaderCircle, X } from "lucide-react";
import {
  createElement,
  type FormEvent,
  type KeyboardEvent,
  useCallback,
  useState,
  useTransition,
} from "react";
import { useDebounce } from "~/hooks/use-debounce";
import {
  SEARCH_FIELD_DROPDOWN_CLASS,
  SEARCH_FIELD_INPUT_CLASS,
  SEARCH_FIELD_WRAPPER_CLASS,
} from "~/components/patterns/search-field-classes";
import { getSpecialtyIcon } from "~/lib/therapy-specialty-icons";
import type { TextRange } from "~/server/services/therapy-specialty-search";
import { cn } from "~/lib/utils";
import { api } from "~/trpc/react";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../ui/command";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";

interface TopicSwitcherProps {
  specialtySlug?: string;
  specialtyLabel?: string;
}

// Stable value cmdk holds when no suggestion is selected. It must be constant:
// a value that changes on every read makes cmdk echo its auto-highlight back as
// a new selection each render, which loops until the call stack overflows.
const NO_SELECTION_VALUE = "topic-switcher-no-selection";

export default function TopicSwitcher({
  specialtySlug,
  specialtyLabel,
}: TopicSwitcherProps) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [isPending, startTransition] = useTransition();
  const [isFocused, setIsFocused] = useState(false);
  const [hasKeyboardSuggestionSelection, setHasKeyboardSuggestionSelection] =
    useState(false);
  const [selectedSuggestionId, setSelectedSuggestionId] = useState<
    string | undefined
  >();
  const [query, setQuery] = useState(specialtyLabel ?? "");
  const debouncedQuery = useDebounce(query, 350);
  const queryIsCurrentTopic =
    Boolean(specialtyLabel) &&
    debouncedQuery.trim().localeCompare(specialtyLabel ?? "", undefined, {
      sensitivity: "accent",
    }) === 0;
  const searchQuery = queryIsCurrentTopic ? "" : debouncedQuery;
  const shouldSearch = isFocused;
  const specialtySearch = api.therapySpecialty.search.useQuery(
    { query: searchQuery },
    { enabled: shouldSearch, staleTime: 5 * 60 * 1000 },
  );
  const shouldSearchTherapists = shouldSearch && searchQuery.trim().length >= 2;
  const therapistSearch = api.therapistRouter.searchSuggestions.useQuery(
    { query: searchQuery },
    { enabled: shouldSearchTherapists, staleTime: 5 * 60 * 1000 },
  );
  const isSearching =
    shouldSearch &&
    (debouncedQuery.trim() !== query.trim() ||
      specialtySearch.isFetching ||
      (shouldSearchTherapists && therapistSearch.isFetching));
  const specialtyResults = specialtySearch.data ?? [];
  const therapistResults = therapistSearch.data ?? [];
  const suggestionIds = [
    ...therapistResults.flatMap((therapist) =>
      therapist.user.name ? [`therapist:${therapist.slug}`] : [],
    ),
    ...specialtyResults.map((specialty) => `topic:${specialty.slug}`),
  ];
  const hasResults = suggestionIds.length > 0;
  const commandValue = selectedSuggestionId ?? NO_SELECTION_VALUE;

  const clearSuggestionSelection = useCallback(() => {
    setHasKeyboardSuggestionSelection(false);
    setSelectedSuggestionId(undefined);
  }, []);

  const handleCommandValueChange = useCallback(
    (nextValue: string) => {
      // Ignore cmdk's echo of the current value, and its auto-highlight of the
      // first item when no keyboard navigation has started. Only a real
      // keyboard selection updates our state, so the echo cannot loop.
      if (nextValue === commandValue) return;
      if (nextValue === NO_SELECTION_VALUE) return;
      if (hasKeyboardSuggestionSelection) {
        setSelectedSuggestionId(nextValue);
      }
    },
    [commandValue, hasKeyboardSuggestionSelection],
  );

  const selectSpecialty = useCallback(
    (nextSlug: string) => {
      const nextQuery = searchParams.toString();
      setIsFocused(false);
      setQuery(specialtyLabel ?? "");
      startTransition(() => {
        router.push(
          `/terapija/${nextSlug}${nextQuery ? `?${nextQuery}` : ""}`,
          {
            scroll: false,
          },
        );
      });
    },
    [router, searchParams, specialtyLabel],
  );

  const selectTherapist = useCallback(
    (slug: string) => {
      setIsFocused(false);
      startTransition(() => {
        router.push(`/terapeuti/${slug}`, { scroll: false });
      });
    },
    [router],
  );

  const navigateToSearch = useCallback(() => {
    const searchTerm = query.trim();
    const nextQuery = new URLSearchParams(searchParams.toString());

    setIsFocused(false);
    startTransition(() => {
      if (searchTerm) {
        nextQuery.set("query", searchTerm);
      } else {
        nextQuery.delete("query");
      }
      router.push(`/terapeuti${nextQuery.size ? `?${nextQuery}` : ""}`, {
        scroll: false,
      });
    });
  }, [query, router, searchParams]);

  const submitSearch = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      navigateToSearch();
    },
    [navigateToSearch],
  );

  const handleSearchKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key === "ArrowDown" || event.key === "ArrowUp") {
        if (suggestionIds.length === 0) return;

        event.preventDefault();
        event.stopPropagation();
        const currentIndex = suggestionIds.indexOf(selectedSuggestionId ?? "");
        const nextIndex =
          event.key === "ArrowDown"
            ? (currentIndex + 1) % suggestionIds.length
            : (currentIndex + suggestionIds.length - 1) % suggestionIds.length;

        setSelectedSuggestionId(suggestionIds[nextIndex]);
        setHasKeyboardSuggestionSelection(true);
        return;
      }

      if (event.key !== "Enter" || hasKeyboardSuggestionSelection) return;

      event.preventDefault();
      event.stopPropagation();
      navigateToSearch();
    },
    [
      hasKeyboardSuggestionSelection,
      navigateToSearch,
      selectedSuggestionId,
      suggestionIds,
    ],
  );

  const handleQueryChange = useCallback(
    (nextQuery: string) => {
      setQuery(nextQuery);
      clearSuggestionSelection();
    },
    [clearSuggestionSelection],
  );

  const clearTopic = useCallback(() => {
    setIsFocused(false);
    const nextQuery = searchParams.toString();
    startTransition(() => {
      router.push(`/terapeuti${nextQuery ? `?${nextQuery}` : ""}`);
    });
  }, [router, searchParams]);

  return (
    <div className="w-full">
      <form onSubmit={submitSearch}>
        <Command
          value={commandValue}
          onValueChange={handleCommandValueChange}
          shouldFilter={false}
          className="relative overflow-visible bg-transparent text-foreground"
        >
          <CommandInput
            aria-label="Pretraži terapeute ili temu"
            placeholder="Pretraži terapeute ili temu"
            wrapperClassName={SEARCH_FIELD_WRAPPER_CLASS}
            className={cn(SEARCH_FIELD_INPUT_CLASS, "pr-16")}
            value={query}
            onValueChange={handleQueryChange}
            onFocus={(event) => {
              event.currentTarget.select();
              clearSuggestionSelection();
              setIsFocused(true);
            }}
            onBlur={() => {
              clearSuggestionSelection();
              window.setTimeout(() => setIsFocused(false), 150);
            }}
            onKeyDownCapture={handleSearchKeyDown}
            disabled={isPending}
          />
          {isSearching && (
            <LoaderCircle
              className={`pointer-events-none absolute top-3 size-4 animate-spin text-muted-foreground ${
                specialtySlug ? "right-10" : "right-3"
              }`}
              aria-label="Pretraživanje"
            />
          )}
          {specialtySlug && (
            <button
              type="button"
              aria-label="Ukloni temu i prikaži sve terapeute"
              className="absolute right-1 top-2 flex size-6 items-center justify-center rounded-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring"
              onMouseDown={(event) => event.preventDefault()}
              onClick={clearTopic}
            >
              <X className="size-4" aria-hidden="true" />
            </button>
          )}
          {shouldSearch && !isSearching && (
            <CommandList
              className={SEARCH_FIELD_DROPDOWN_CLASS}
              onMouseDown={(event) => event.preventDefault()}
            >
              {specialtySearch.isError && !hasResults ? (
                <div className="space-y-2 px-4 py-5 text-center">
                  <p className="text-sm text-muted-foreground">
                    Nismo uspeli da učitamo teme.
                  </p>
                  <button
                    type="button"
                    className="text-sm font-medium text-brand underline-offset-4 hover:underline focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring"
                    onClick={() => {
                      void specialtySearch.refetch();
                    }}
                  >
                    Pokušaj ponovo
                  </button>
                </div>
              ) : !hasResults && !therapistSearch.isFetching ? (
                <CommandEmpty>Nema odgovarajućih rezultata.</CommandEmpty>
              ) : null}
              {therapistResults.length > 0 ? (
                <CommandGroup heading="Terapeuti">
                  {therapistResults.flatMap((therapist) =>
                    therapist.user.name
                      ? [
                          <CommandItem
                            key={therapist.slug}
                            value={`therapist:${therapist.slug}`}
                            onSelect={() => selectTherapist(therapist.slug)}
                            className="items-start py-2"
                          >
                            <Avatar className="mt-0.5 size-8 border border-border">
                              <AvatarImage
                                src={therapist.user.image ?? undefined}
                                alt=""
                              />
                              <AvatarFallback className="bg-secondary text-xs font-medium text-brand">
                                {therapist.user.name.slice(0, 1)}
                              </AvatarFallback>
                            </Avatar>
                            <span className="min-w-0">
                              <span className="block font-medium">
                                <QueryHighlightedText
                                  text={therapist.user.name}
                                  query={searchQuery}
                                />
                              </span>
                              {therapist.title ? (
                                <span className="mt-0.5 line-clamp-2 block text-xs leading-relaxed text-muted-foreground">
                                  <QueryHighlightedText
                                    text={therapist.title}
                                    query={searchQuery}
                                  />
                                </span>
                              ) : null}
                            </span>
                          </CommandItem>,
                        ]
                      : [],
                  )}
                </CommandGroup>
              ) : null}
              {!specialtySearch.isError && specialtyResults.length > 0 ? (
                <CommandGroup
                  heading={searchQuery.trim() ? "Teme" : "Sve teme"}
                >
                  {specialtyResults.map((specialty) => (
                    <CommandItem
                      key={specialty.slug}
                      value={`topic:${specialty.slug}`}
                      onSelect={() => selectSpecialty(specialty.slug)}
                      className="items-start py-2"
                    >
                      <SpecialtyPickerIcon iconId={specialty.iconId} />
                      <span className="min-w-0">
                        <span className="block font-medium">
                          <HighlightedText
                            text={specialty.label}
                            ranges={specialty.labelRanges}
                          />
                        </span>
                        <span className="mt-0.5 line-clamp-2 block text-xs leading-relaxed text-muted-foreground">
                          <HighlightedText
                            text={specialty.description}
                            ranges={specialty.descriptionRanges}
                          />
                        </span>
                      </span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              ) : null}
            </CommandList>
          )}
        </Command>
      </form>
    </div>
  );
}

function SpecialtyPickerIcon({
  iconId,
}: {
  iconId: Parameters<typeof getSpecialtyIcon>[0];
}) {
  return createElement(getSpecialtyIcon(iconId), {
    className: "size-4 shrink-0 text-brand",
    "aria-hidden": true,
  });
}

function HighlightedText({
  text,
  ranges,
}: {
  text: string;
  ranges: TextRange[];
}) {
  if (!ranges.length) return text;

  const parts: React.ReactNode[] = [];
  let currentIndex = 0;

  for (const [start, end] of ranges) {
    if (start > currentIndex) {
      parts.push(text.slice(currentIndex, start));
    }

    parts.push(
      <mark
        key={`${start}-${end}`}
        className="rounded-sm bg-brand/20 px-0.5 font-semibold text-foreground"
      >
        {text.slice(start, end + 1)}
      </mark>,
    );
    currentIndex = end + 1;
  }

  if (currentIndex < text.length) {
    parts.push(text.slice(currentIndex));
  }

  return <>{parts}</>;
}

function QueryHighlightedText({
  text,
  query,
}: {
  text: string;
  query: string;
}) {
  const normalizedQuery = query.trim();
  if (!normalizedQuery) return text;

  const matcher = new RegExp(
    `(${normalizedQuery.replace(/[.*+?^${}()|[\\]\\\\]/g, "\\$&")})`,
    "i",
  );
  const parts = text.split(matcher);

  return parts.map((part, index) =>
    index % 2 === 1 ? (
      <mark
        key={`${part}-${index}`}
        className="rounded-sm bg-brand/20 px-0.5 font-semibold text-foreground"
      >
        {part}
      </mark>
    ) : (
      part
    ),
  );
}
